import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { Deal, DealStage, Proposta, PropostaItem } from '@/types'
import api from '@/services/api'
import { normalizeProposalsList } from '@/utils/proposalApi'
import { useAuthStore } from '@/stores/authStore'
import { useDealsStore } from '@/stores/useDealsStore'
import { handleDealStageMoveError } from '@/utils/handleDealStageMoveError'
import { mapDealFromApi } from '@/adapters/deals'
import { resolveStageTransitionIntent } from '@/services/dealStageTransitionFlow'
import { patchDealStage } from '@/services/patchDealStage'
import { getStageLabel, isProposalTabVisible } from '../deals-constants'
import {
  getDefaultPanelTab,
  getMissingRequirements,
  resolveFooterPrimary,
  resolvePrimaryDecision,
  scrollToDealSection,
  STAGE_SECTION_IDS,
  type PanelTab,
} from './dealDetailPanelConfig'
import { hasProposalContent } from './DealProposalTab/proposalGuard'
import {
  getMissingContractDataFields,
  getMissingHandoverCompletionFields,
  isContractDataComplete,
  type ContractDataForm,
  type ContractDataModalMode,
} from './contractDataUtils'
import { loadContractDataFormForDeal, persistContractDataForDeal } from './contractDataFlow'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { isFinanceQueueRole } from '@/utils/dealFinanceStageGate'

export type DealProposalGuardState = {
  proposals: Proposta[]
  draftItens: PropostaItem[]
}

export type DealProposalPanelActions = {
  flushSave: (status?: string) => Promise<boolean>
}

export type ReuniaoFooterAction = {
  confirm: () => void
  disabled: boolean
  confirming: boolean
}

function proposalFlushStatusForStage(
  targetStage: DealStage,
  currentEtapa: DealStage,
): string | null {
  if (
    targetStage === 'proposta_enviada' &&
    ['proposta_elaboracao', 'renegociacao', 'ajustar_proposta'].includes(currentEtapa)
  ) {
    return 'enviada'
  }
  if (targetStage === 'gerar_contrato' && currentEtapa === 'ajustar_proposta') {
    return 'enviada'
  }
  return null
}

export function useDealDetailPanel(
  deal: Deal | null,
  onDealUpdated?: (deal: Deal) => void,
  proposalGuard: DealProposalGuardState = { proposals: [], draftItens: [] },
  onClose?: () => void,
  proposalActions?: DealProposalPanelActions | null,
) {
  const { user } = useAuthStore()
  const dealPanelHighlight = useDealsStore((s) => s.dealPanelHighlight)
  const setDealPanelHighlight = useDealsStore((s) => s.setDealPanelHighlight)
  const clearDealPanelHighlight = useDealsStore((s) => s.clearDealPanelHighlight)
  const setDealContractModalOpen = useDealsStore((s) => s.setDealContractModalOpen)

  const scrollRef = useRef<HTMLDivElement>(null)
  const reuniaoConfirmRef = useRef<ReuniaoFooterAction | null>(null)
  const pendingReuniaoConfirmRef = useRef(false)
  const [reuniaoConfirm, setReuniaoConfirm] = useState<ReuniaoFooterAction | null>(null)
  const [trackedDealId, setTrackedDealId] = useState(deal?.id)
  if (deal?.id !== trackedDealId) {
    setTrackedDealId(deal?.id)
    reuniaoConfirmRef.current = null
    pendingReuniaoConfirmRef.current = false
    setReuniaoConfirm(null)
  }
  const [currentDeal, setCurrentDeal] = useState<Deal | null>(deal)
  const [activeTab, setActiveTab] = useState<PanelTab>('info')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showLossModal, setShowLossModal] = useState(false)
  const [showFutureModal, setShowFutureModal] = useState(false)
  const [showWinModal, setShowWinModal] = useState(false)
  const [showStageChoice, setShowStageChoice] = useState(false)
  const [showContractDataModal, setShowContractDataModal] = useState(false)
  const [contractDataModalMode, setContractDataModalMode] =
    useState<ContractDataModalMode>('gerar_contrato')
  const [contractDataDeal, setContractDataDeal] = useState<Deal | null>(null)
  const [blockedRequirements, setBlockedRequirements] = useState<string[]>([])
  const [loadedProposals, setLoadedProposals] = useState<Proposta[]>([])

  useEffect(() => {
    const baseDeal = deal
    let cancelled = false

    const needsDetailFetch =
      baseDeal &&
      ['reuniao_agendada', 'reuniao_realizada', 'proposta_elaboracao', 'proposta_enviada'].includes(
        baseDeal.etapa,
      ) &&
      !baseDeal.reunioes?.length

    async function fetchFullDeal() {
      if (!baseDeal || !needsDetailFetch) return
      try {
        const { data } = await api.get(`/deals/${baseDeal.id}`)
        const fullDeal = mapDealFromApi(data.data ?? data)
        if (!cancelled) setCurrentDeal({ ...baseDeal, ...fullDeal })
      } catch {
        if (!cancelled) setCurrentDeal(baseDeal)
      }
    }

    setCurrentDeal(baseDeal)
    setActiveTab(getDefaultPanelTab(baseDeal?.etapa))
    if (needsDetailFetch) void fetchFullDeal()

    return () => {
      cancelled = true
    }
  }, [deal])

  useEffect(() => {
    if (!currentDeal) {
      setLoadedProposals([])
      return
    }
    if (!['proposta_elaboracao', 'renegociacao', 'ajustar_proposta'].includes(currentDeal.etapa)) {
      setLoadedProposals([])
      return
    }

    let cancelled = false
    api
      .get(`/deals/${currentDeal.id}/proposals`)
      .then(({ data }) => {
        if (!cancelled) setLoadedProposals(normalizeProposalsList(data))
      })
      .catch(() => {
        if (!cancelled) setLoadedProposals([])
      })

    return () => {
      cancelled = true
    }
  }, [currentDeal?.id, currentDeal?.etapa])

  const effectiveProposalGuard = useMemo<DealProposalGuardState>(
    () => ({
      proposals:
        proposalGuard.proposals.length > 0 ? proposalGuard.proposals : loadedProposals,
      draftItens: proposalGuard.draftItens,
    }),
    [proposalGuard, loadedProposals],
  )

  useEffect(() => {
    if (currentDeal && !isProposalTabVisible(currentDeal.etapa) && activeTab === 'proposal') {
      setActiveTab('info')
    }
  }, [currentDeal?.etapa, activeTab])

  useEffect(() => {
    setDealContractModalOpen(showContractDataModal)
    return () => setDealContractModalOpen(false)
  }, [showContractDataModal, setDealContractModalOpen])

  const handleDealPatch = useCallback(
    (updates: Partial<Deal>) => {
      if (!currentDeal) return
      const updated = { ...currentDeal, ...updates }
      setCurrentDeal(updated)
      if (updates.etapa === 'proposta_elaboracao' || updates.etapa === 'renegociacao' || updates.etapa === 'ajustar_proposta') {
        setActiveTab('proposal')
      }
      onDealUpdated?.(updated)
    },
    [currentDeal, onDealUpdated],
  )

  const decision = useMemo(
    () => (currentDeal ? resolvePrimaryDecision(currentDeal) : undefined),
    [currentDeal],
  )

  const openContractDataModal = useCallback(
    (dealSnapshot: Deal, mode: ContractDataModalMode = 'gerar_contrato') => {
      setContractDataDeal(dealSnapshot)
      setContractDataModalMode(mode)
      setShowContractDataModal(true)
    },
    [],
  )

  const patchStageCore = useCallback(
    async (
      targetStage: DealStage,
      contractForm?: ContractDataForm,
      options?: { suppressSuccessToast?: boolean },
    ): Promise<boolean> => {
      if (!currentDeal) return false

      if (targetStage === 'gerar_contrato') {
        const form = contractForm ?? (await loadContractDataFormForDeal(currentDeal))
        if (!isContractDataComplete(form)) {
          openContractDataModal(currentDeal)
          toast.error(
            `Complete os dados do contrato: ${getMissingContractDataFields(form).join(', ')}`,
          )
          return false
        }
      }

      const missing = getMissingRequirements(currentDeal, targetStage)
      if (missing.length > 0) {
        setBlockedRequirements(missing)
        return false
      }

      const proposalFlushStatus = proposalFlushStatusForStage(targetStage, currentDeal.etapa)
      if (
        proposalFlushStatus &&
        effectiveProposalGuard.draftItens.length > 0 &&
        proposalActions?.flushSave
      ) {
        setActiveTab('proposal')
        const flushed = await proposalActions.flushSave(proposalFlushStatus)
        if (!flushed) return false
      }

      if (
        targetStage === 'proposta_enviada' &&
        (currentDeal.etapa === 'proposta_elaboracao' || currentDeal.etapa === 'renegociacao' || currentDeal.etapa === 'ajustar_proposta') &&
        !hasProposalContent(effectiveProposalGuard.proposals, effectiveProposalGuard.draftItens)
      ) {
        setActiveTab('proposal')
        toast.error('Adicione pelo menos um plano à proposta antes de marcar como enviada')
        return false
      }

      const intent = resolveStageTransitionIntent(currentDeal, targetStage, user?.role)
      if (intent.type === 'blocked') {
        if (intent.code === 'REUNIAO_REQUIRED') {
          setActiveTab('info')
          setDealPanelHighlight({
            dealId: currentDeal.id,
            fields: intent.highlightFields ?? ['dataReuniao'],
          })
        }
        toast.error(intent.reason)
        return false
      }
      if (intent.type === 'open_loss_modal') {
        setShowLossModal(true)
        return false
      }
      if (intent.type === 'open_stage_choice') {
        setShowStageChoice(true)
        return false
      }
      if (intent.type === 'open_future_modal') {
        setShowFutureModal(true)
        return false
      }
      if (intent.type === 'open_win_modal') {
        if (currentDeal.etapa === 'handover') {
          openContractDataModal(currentDeal, 'handover_complete')
        } else {
          setShowWinModal(true)
        }
        return false
      }
      if (intent.type !== 'patch') return false

      try {
        const updated = await patchDealStage(
          intent.dealId,
          intent.targetStage,
          intent.qualification,
        )
        handleDealPatch(updated)
        if (!options?.suppressSuccessToast) {
          toast.success(`Etapa alterada para "${getStageLabel(targetStage)}"`)
        }
        return true
      } catch (error: unknown) {
        setActiveTab('info')
        handleDealStageMoveError(
          error,
          currentDeal,
          {
            setSelectedDeal: (d) => setCurrentDeal(d),
            setDealPanelHighlight,
            toastError: toast.error,
          },
          'Erro ao alterar etapa',
        )
        return false
      }
    },
    [currentDeal, handleDealPatch, setDealPanelHighlight, effectiveProposalGuard, openContractDataModal, proposalActions],
  )

  const tryAdvanceToGerarContrato = useCallback(
    async (dealSnapshot: Deal) => {
      if (
        dealSnapshot.etapa === 'ajustar_proposta' &&
        effectiveProposalGuard.draftItens.length > 0 &&
        proposalActions?.flushSave
      ) {
        setActiveTab('proposal')
        const flushed = await proposalActions.flushSave('enviada')
        if (!flushed) return
      }

      setShowStageChoice(false)
      openContractDataModal(dealSnapshot, 'gerar_contrato')
    },
    [effectiveProposalGuard.draftItens.length, openContractDataModal, proposalActions],
  )

  const patchStage = useCallback(
    async (targetStage: DealStage) => {
      if (!currentDeal) return
      if (targetStage === 'ganho' && currentDeal.etapa === 'handover') {
        openContractDataModal(currentDeal, 'handover_complete')
        return
      }
      if (targetStage === 'gerar_contrato') {
        await tryAdvanceToGerarContrato(currentDeal)
        return
      }
      await patchStageCore(targetStage)
    },
    [currentDeal, tryAdvanceToGerarContrato, patchStageCore, openContractDataModal],
  )

  const handleContractDataConfirm = useCallback(
    async (form: ContractDataForm) => {
      if (!currentDeal) return

      if (contractDataModalMode === 'handover_complete') {
        const missing = getMissingHandoverCompletionFields(form)
        if (missing.length > 0) {
          toast.error(`Preencha todos os dados: ${missing.join(', ')}`)
          return
        }

        try {
          const updated = await patchDealStage(currentDeal.id, 'ganho', {
            dataFechamento: new Date(form.dataFechamentoHandover).toISOString(),
            observacoes: form.observacoesHandover.trim(),
          })
          handleDealPatch(updated)
          setShowContractDataModal(false)
          toast.success('Handover concluído — deal registrado como ganho')
          onClose?.()
        } catch (err: unknown) {
          toast.error(getFriendlyError(err, 'Erro ao concluir handover'))
        }
        return
      }

      const missing = getMissingContractDataFields(form)
      if (missing.length > 0) {
        toast.error(`Preencha todos os dados: ${missing.join(', ')}`)
        return
      }

      try {
        const { deal: updatedDealFields } = await persistContractDataForDeal(currentDeal, form)
        handleDealPatch(updatedDealFields)

        if (
          currentDeal.etapa === 'ajustar_proposta' &&
          effectiveProposalGuard.draftItens.length > 0 &&
          proposalActions?.flushSave
        ) {
          setActiveTab('proposal')
          const flushed = await proposalActions.flushSave('enviada')
          if (!flushed) return
        }

        const advanced = await patchStageCore('gerar_contrato', form, {
          suppressSuccessToast: true,
        })
        if (!advanced) return

        setShowContractDataModal(false)
        toast.success('Dados enviados. Aguardando retorno do financeiro para continuar.')
        onClose?.()
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao salvar dados do contrato'))
      }
    },
    [currentDeal, handleDealPatch, patchStageCore, onClose, contractDataModalMode, effectiveProposalGuard.draftItens.length, proposalActions],
  )

  const handlePrimaryAction = useCallback(() => {
    if (!currentDeal || !decision) return
    if (currentDeal.etapa === 'proposta_enviada') {
      setShowStageChoice(true)
      return
    }
    if (!decision.targetStage || decision.targetStage === currentDeal.etapa) return
    void patchStage(decision.targetStage)
  }, [currentDeal, decision, patchStage])

  const registerReuniaoConfirm = useCallback((action: ReuniaoFooterAction | null) => {
    reuniaoConfirmRef.current = action
    setReuniaoConfirm(action)
    if (action && pendingReuniaoConfirmRef.current) {
      pendingReuniaoConfirmRef.current = false
      action.confirm()
    }
  }, [])

  const handleFooterPrimary = useCallback(() => {
    if (!currentDeal || !decision) return

    if (currentDeal.etapa === 'reuniao_agendada') {
      setActiveTab('info')
      requestAnimationFrame(() => {
        scrollToDealSection(STAGE_SECTION_IDS.reuniaoAgendada, scrollRef.current)
      })
      if (reuniaoConfirmRef.current) {
        reuniaoConfirmRef.current.confirm()
      } else {
        pendingReuniaoConfirmRef.current = true
      }
      return
    }

    const footer = resolveFooterPrimary(currentDeal, decision)
    if (!footer) return

    if (footer.mode === 'scroll') {
      setActiveTab('info')
      requestAnimationFrame(() => {
        scrollToDealSection(footer.scrollTargetId, scrollRef.current)
      })
      return
    }

    handlePrimaryAction()
  }, [currentDeal, decision, handlePrimaryAction])

  const handleReopenRenegotiation = useCallback(() => {
    void patchStage('renegociacao')
  }, [patchStage])

  const handleStageChoice = useCallback(
    async (targetStage: DealStage) => {
      if (targetStage === 'perdido') {
        setShowStageChoice(false)
        setShowLossModal(true)
        return
      }
      setShowStageChoice(false)
      if (targetStage === 'gerar_contrato' && currentDeal) {
        await tryAdvanceToGerarContrato(currentDeal)
        return
      }
      await patchStageCore(targetStage)
      if (targetStage === 'renegociacao' || targetStage === 'ajustar_proposta') setActiveTab('proposal')
    },
    [currentDeal, tryAdvanceToGerarContrato, patchStageCore],
  )

  const privilegedRole = ['ADMIN', 'MANAGER'].includes(user?.role?.toUpperCase() ?? '')
  const isClosed = currentDeal ? ['ganho', 'perdido'].includes(currentDeal.etapa) : false
  const canDelete =
    Boolean(currentDeal) &&
    privilegedRole &&
    ['qualificado', 'reuniao_agendada', 'reuniao_realizada', 'proposta_elaboracao'].includes(
      currentDeal!.etapa,
    )
  const canScheduleFuture =
    Boolean(currentDeal) &&
    !isClosed &&
    !['pausado', 'fechamento_futuro'].includes(currentDeal!.etapa)

  const footerPrimary =
    currentDeal && decision && !(currentDeal.etapa === 'gerar_contrato' && !isFinanceQueueRole(user?.role))
      ? resolveFooterPrimary(currentDeal, decision)
      : null
  const footerPrimaryDisabled = Boolean(
    (currentDeal?.etapa === 'reuniao_agendada' && reuniaoConfirm?.disabled) ||
    (footerPrimary?.mode === 'action' &&
      ((decision?.targetStage === 'proposta_enviada' &&
        (currentDeal?.etapa === 'proposta_elaboracao' || currentDeal?.etapa === 'renegociacao') &&
        !hasProposalContent(effectiveProposalGuard.proposals, effectiveProposalGuard.draftItens)) ||
        (decision?.targetStage === 'gerar_contrato' &&
          currentDeal?.etapa === 'ajustar_proposta' &&
          !hasProposalContent(effectiveProposalGuard.proposals, effectiveProposalGuard.draftItens)))),
  )

  const panelHighlightFields =
    currentDeal && dealPanelHighlight?.dealId === currentDeal.id
      ? dealPanelHighlight.fields
      : []

  return {
    scrollRef,
    currentDeal,
    activeTab,
    setActiveTab,
    showDeleteModal,
    setShowDeleteModal,
    showLossModal,
    setShowLossModal,
    showFutureModal,
    setShowFutureModal,
    showWinModal,
    setShowWinModal,
    showStageChoice,
    setShowStageChoice,
    showContractDataModal,
    setShowContractDataModal,
    contractDataModalMode,
    contractDataDeal,
    blockedRequirements,
    setBlockedRequirements,
    clearDealPanelHighlight,
    handleDealPatch,
    registerReuniaoConfirm,
    handleFooterPrimary,
    handleReopenRenegotiation,
    handleStageChoice,
    handleContractDataConfirm,
    footerPrimary,
    footerPrimaryDisabled,
    footerPrimaryLoading: Boolean(
      currentDeal?.etapa === 'reuniao_agendada' && reuniaoConfirm?.confirming,
    ),
    canDelete,
    canScheduleFuture,
    isClosed,
    panelHighlightFields,
  }
}
